/**
 * persistence/vcs/vcsFirestore.js
 *
 * The "push / pull" half of the git-style layer — mirrors a branch (and its
 * commits) to Firestore so another analyst can pull it in and merge.
 *
 * Firestore data model (nested under the existing session document, reusing
 * the same `{analystId}_{matchId}` doc-id convention as firestoreSync.js):
 *
 *   /analystSessions/{analystId}_{matchId}/branches/{branchId}
 *       branchName      string
 *       baseBranchName  string|null
 *       headCommitId    string|null
 *       stateUpdate     string  (base64 of Y.encodeStateAsUpdate)
 *       eventCount      number
 *       deviceId        string  (device that last wrote the HEAD)
 *       updatedAt       Timestamp
 *
 *   /analystSessions/{analystId}_{matchId}/commits/{commitId}
 *       commitId, branchName, parentCommitId, message, authorAnalystId,
 *       eventCount, diffSummary, createdAt, stateUpdate (base64)
 *
 * Push/pull are user-triggered (buttons), so unlike the hot session-backup
 * path they may talk to Firestore directly rather than via the IDB queue.
 *
 * Branch names contain "/" (e.g. "me/123"), which Firestore forbids in a doc
 * id, so they are encoded with `_encodeBranchId`.
 */

import { getFirestoreClient } from "../firestore/firestoreClient";
import {
  FIRESTORE_SESSIONS_COLLECTION,
  FIRESTORE_BRANCHES_SUBCOLLECTION,
  FIRESTORE_COMMITS_SUBCOLLECTION,
  VCS_CLOUD_COMMITS_MAX,
} from "../config";
import {
  loadDoc,
  encodeDoc,
  docToString,
  mergeStateInto,
  sharesHistory,
  encodeUpdateBase64,
  decodeUpdateBase64,
} from "./yjsDoc";
import { getClientInfo } from "../clientInfo";
import { countEvents } from "../managers/DiffManager";
import {
  getBranch,
  getCommits,
  createBranch,
  defaultBranchName,
} from "./BranchManager";
import { commitsTable } from "../db/database";

// ─── Push ─────────────────────────────────────────────────────────────────────

/**
 * Pushes a local branch HEAD + its un-pushed commits to Firestore under the
 * pushing analyst's own session document.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string=} params.branchName  defaults to the analyst's own branch
 * @returns {Promise<{branchName: string, commitsPushed: number}>}
 */
export async function pushBranch({ matchId, analystId, branchName }) {
  const db = await getFirestoreClient();
  if (!db) throw new Error("Firestore client not available — check Firebase config.");

  const name = branchName ?? defaultBranchName(analystId);
  const branch = await getBranch(matchId, analystId, name);
  if (!branch) throw new Error(`Branch "${name}" not found locally.`);

  const { doc, setDoc, Timestamp } = await import("firebase/firestore");

  const sessionId = _sessionDocId(matchId, analystId);

  // 1. Branch HEAD document.  Share is an explicit user action, so it may
  //    replace a cloud HEAD from another device that it can't merge with.
  const branchRef = doc(
    db,
    FIRESTORE_SESSIONS_COLLECTION,
    sessionId,
    FIRESTORE_BRANCHES_SUBCOLLECTION,
    _encodeBranchId(name)
  );
  await _writeBranchHead({
    db,
    branchRef,
    name,
    branch,
    headCommitId: branch.headCommitId ?? null,
    force: true,
  });

  // 2. Un-pushed commits (cap to a sensible recent window).
  const commits = await getCommits(matchId, analystId, name); // newest-first
  const toPush = commits.filter((c) => !c.pushed).reverse(); // oldest-first
  for (const c of toPush) {
    const commitRef = doc(
      db,
      FIRESTORE_SESSIONS_COLLECTION,
      sessionId,
      FIRESTORE_COMMITS_SUBCOLLECTION,
      c.commitId
    );
    await setDoc(commitRef, {
      commitId: c.commitId,
      branchName: name,
      parentCommitId: c.parentCommitId ?? null,
      message: c.message ?? "",
      authorAnalystId: c.authorAnalystId ?? analystId,
      eventCount: c.eventCount ?? 0,
      diffSummary: c.diffSummary ?? { added: 0, removed: 0 },
      triggerType: c.triggerType ?? "unknown",
      versionNumber: c.versionNumber ?? null,
      sources: c.sources ?? [],
      client: c.client ?? null,
      createdAt: Timestamp.fromMillis(c.createdAt ?? Date.now()),
      stateUpdate: encodeUpdateBase64(c.stateUpdate),
    });
    // Best-effort flip pushed flag locally.
    try {
      await commitsTable().update(c.id, { pushed: true });
    } catch {
      /* non-fatal */
    }
  }

  return { branchName: name, commitsPushed: toPush.length };
}

// ─── Push a single commit (checkpoint funnel) ──────────────────────────────────

/**
 * Pushes ONE commit + the branch HEAD to Firestore.  Called (gated) from the
 * checkpoint funnel so auto-commits reach the cloud within the Firestore
 * budget, and immediately for manual/exit/run-analysis commits.
 *
 * Writes: 1 commit doc + 1 branch upsert, then prunes the branch's cloud
 * commits to VCS_CLOUD_COMMITS_MAX.  Flips the local commit's `pushed` flag.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {object} params.commit  the commit row returned by BranchManager.commit
 */
export async function pushCommit({ matchId, analystId, commit }) {
  const db = await getFirestoreClient();
  if (!db || !commit) return;

  const name = commit.branchName ?? defaultBranchName(analystId);
  try {
    const {
      doc, setDoc, getDocs, deleteDoc, collection, query, orderBy, Timestamp,
    } = await import("firebase/firestore");
    const sessionId = _sessionDocId(matchId, analystId);

    // 1. Branch HEAD (so cross-device fetch gets the latest state).  Merged
    //    with the cloud HEAD, never blindly overwritten — see _writeBranchHead.
    let headConflict = false;
    const branch = await getBranch(matchId, analystId, name);
    if (branch) {
      const branchRef = doc(
        db, FIRESTORE_SESSIONS_COLLECTION, sessionId, FIRESTORE_BRANCHES_SUBCOLLECTION, _encodeBranchId(name)
      );
      try {
        ({ conflict: headConflict } = await _writeBranchHead({
          db,
          branchRef,
          name,
          branch,
          headCommitId: branch.headCommitId ?? commit.commitId,
          force: false,
        }));
      } catch (err) {
        console.warn("[vcsFirestore] branch HEAD update failed:", err.message);
      }
    }

    // 2. Commit document — always written, so history is complete even when
    //    the HEAD was left alone.
    const commitRef = doc(
      db, FIRESTORE_SESSIONS_COLLECTION, sessionId, FIRESTORE_COMMITS_SUBCOLLECTION, commit.commitId
    );
    await setDoc(commitRef, {
      commitId: commit.commitId,
      branchName: name,
      parentCommitId: commit.parentCommitId ?? null,
      message: commit.message ?? "",
      authorAnalystId: commit.authorAnalystId ?? analystId,
      triggerType: commit.triggerType ?? "unknown",
      versionNumber: commit.versionNumber ?? null,
      eventCount: commit.eventCount ?? 0,
      diffSummary: commit.diffSummary ?? { added: 0, removed: 0 },
      sources: commit.sources ?? [],
      client: commit.client ?? null,
      headConflict,
      createdAt: Timestamp.fromMillis(commit.createdAt ?? Date.now()),
      stateUpdate: encodeUpdateBase64(commit.stateUpdate),
    });

    // 3. Flip the local pushed flag (best-effort).
    if (commit.id != null) {
      try {
        await commitsTable().update(commit.id, { pushed: true });
      } catch {
        /* non-fatal */
      }
    }

    // 4. Prune the branch's cloud commits to the cap (oldest-first).
    try {
      const commitsRef = collection(
        db, FIRESTORE_SESSIONS_COLLECTION, sessionId, FIRESTORE_COMMITS_SUBCOLLECTION
      );
      const snap = await getDocs(query(commitsRef, orderBy("createdAt", "asc")));
      const branchDocs = snap.docs.filter((d) => d.data().branchName === name);
      const excess = branchDocs.length - VCS_CLOUD_COMMITS_MAX;
      if (excess > 0) {
        await Promise.all(branchDocs.slice(0, excess).map((d) => deleteDoc(d.ref)));
      }
    } catch {
      /* non-fatal — the commit was written */
    }
  } catch (err) {
    console.warn("[vcsFirestore] pushCommit failed:", err.message);
  }
}

// ─── Fetch own branch (cross-device) ───────────────────────────────────────────

/**
 * Fetches the analyst's OWN pushed branch + its commits from Firestore, so a
 * second device can re-hydrate the working branch and commit log.
 *
 * Returns decoded binary state ready to merge locally, or null if this analyst
 * has never pushed this branch.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string=} params.branchName  defaults to the analyst's own branch
 * @returns {Promise<{branchName, headCommitId, stateUpdate: Uint8Array, commits: object[]}|null>}
 */
export async function fetchOwnBranch({ matchId, analystId, branchName }) {
  const db = await getFirestoreClient();
  if (!db) return null;

  const name = branchName ?? defaultBranchName(analystId);
  try {
    const { doc, getDoc, collection, getDocs } = await import("firebase/firestore");
    const sessionId = _sessionDocId(matchId, analystId);

    const branchRef = doc(
      db,
      FIRESTORE_SESSIONS_COLLECTION,
      sessionId,
      FIRESTORE_BRANCHES_SUBCOLLECTION,
      _encodeBranchId(name)
    );
    const branchSnap = await getDoc(branchRef);
    if (!branchSnap.exists()) return null;
    const bdata = branchSnap.data();

    // All commits under this session belong to this analyst; filter to the
    // branch client-side so no composite Firestore index is required.
    const commitsRef = collection(
      db,
      FIRESTORE_SESSIONS_COLLECTION,
      sessionId,
      FIRESTORE_COMMITS_SUBCOLLECTION
    );
    const commitsSnap = await getDocs(commitsRef);
    const commits = commitsSnap.docs
      .map((d) => d.data())
      .filter((c) => c.branchName === name)
      .map((c) => ({
        commitId: c.commitId,
        branchName: c.branchName,
        parentCommitId: c.parentCommitId ?? null,
        message: c.message ?? "",
        authorAnalystId: c.authorAnalystId ?? analystId,
        eventCount: c.eventCount ?? 0,
        diffSummary: c.diffSummary ?? { added: 0, removed: 0 },
        triggerType: c.triggerType ?? "unknown",
        versionNumber: c.versionNumber ?? null,
        sources: c.sources ?? [],
        client: c.client ?? null,
        headConflict: Boolean(c.headConflict),
        createdAt: c.createdAt?.toMillis?.() ?? Date.now(),
        stateUpdate: decodeUpdateBase64(c.stateUpdate),
      }));

    return {
      branchName: name,
      headCommitId: bdata.headCommitId ?? null,
      stateUpdate: decodeUpdateBase64(bdata.stateUpdate),
      commits,
    };
  } catch (err) {
    console.warn("[vcsFirestore] fetchOwnBranch failed:", err.message);
    return null;
  }
}

// ─── Pull ─────────────────────────────────────────────────────────────────────

/**
 * Lists branches another analyst has pushed for this match.
 *
 * @param {string} matchId
 * @param {string} ownerAnalystId  the analyst whose pushed branches we want
 * @returns {Promise<Array<{branchName, headCommitId, eventCount, updatedAt}>>}
 */
export async function listRemoteBranches(matchId, ownerAnalystId) {
  const db = await getFirestoreClient();
  if (!db) return [];
  try {
    const { collection, getDocs } = await import("firebase/firestore");
    const ref = collection(
      db,
      FIRESTORE_SESSIONS_COLLECTION,
      _sessionDocId(matchId, ownerAnalystId),
      FIRESTORE_BRANCHES_SUBCOLLECTION
    );
    const snap = await getDocs(ref);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        branchName: data.branchName,
        headCommitId: data.headCommitId ?? null,
        eventCount: data.eventCount ?? 0,
        updatedAt: data.updatedAt?.toMillis?.() ?? null,
        ownerAnalystId,
      };
    });
  } catch (err) {
    console.warn("[vcsFirestore] listRemoteBranches failed:", err.message);
    return [];
  }
}

/**
 * Pulls another analyst's pushed branch into the current analyst's local store
 * as a read-only source branch ready to be merged.
 *
 * The local branch is named `pull/{ownerAnalystId}/{sourceBranch}` so it never
 * collides with the analyst's own working branch.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId        the puller (current analyst)
 * @param {string} params.ownerAnalystId   the source analyst
 * @param {string} params.branchName       the source branch name
 * @returns {Promise<object|null>} the created local branch row
 */
export async function pullBranch({ matchId, analystId, ownerAnalystId, branchName }) {
  const db = await getFirestoreClient();
  if (!db) throw new Error("Firestore client not available — check Firebase config.");

  const { doc, getDoc } = await import("firebase/firestore");
  const branchRef = doc(
    db,
    FIRESTORE_SESSIONS_COLLECTION,
    _sessionDocId(matchId, ownerAnalystId),
    FIRESTORE_BRANCHES_SUBCOLLECTION,
    _encodeBranchId(branchName)
  );
  const snap = await getDoc(branchRef);
  if (!snap.exists()) throw new Error("Remote branch not found — has it been pushed?");

  const data = snap.data();
  const localName = `pull/${ownerAnalystId}/${branchName}`;
  const stateUpdate = decodeUpdateBase64(data.stateUpdate);

  return createBranch({
    matchId,
    analystId,
    branchName: localName,
    baseBranchName: branchName,
    stateUpdate,
    headCommitId: data.headCommitId ?? null,
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function _sessionDocId(matchId, analystId) {
  return `${analystId}_${matchId}`;
}

/** Firestore doc ids cannot contain "/", so encode branch names. */
function _encodeBranchId(branchName) {
  return branchName.replace(/\//g, "~");
}

/**
 * Writes a branch HEAD in a transaction, merging with what is already in the
 * cloud instead of overwriting it, so one device can't wipe out another's work.
 *
 *   - no cloud HEAD, or an empty one        → write ours
 *   - cloud HEAD shares history with ours   → write the CRDT merge of both
 *   - unrelated history from another device → leave the cloud HEAD alone
 *     (merging would concatenate both event lists) unless `force` is set
 *
 * @returns {Promise<{conflict: boolean}>}  conflict: the cloud HEAD was kept
 */
async function _writeBranchHead({ db, branchRef, name, branch, headCommitId, force }) {
  const { runTransaction, serverTimestamp } = await import("firebase/firestore");
  const { deviceId } = getClientInfo();
  const ours = branch.stateUpdate;

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(branchRef);
    const cloud = snap.exists() ? snap.data() : null;
    const cloudState = cloud?.stateUpdate ? decodeUpdateBase64(cloud.stateUpdate) : null;
    const cloudHasEvents =
      cloudState !== null && countEvents(docToString(loadDoc(cloudState))) > 0;

    let head = ours;
    if (cloudHasEvents && sharesHistory(cloudState, ours)) {
      const merged = loadDoc(ours);
      mergeStateInto(merged, cloudState);
      head = encodeDoc(merged);
    } else if (cloudHasEvents && !force && cloud.deviceId !== deviceId) {
      return { conflict: true };
    }

    tx.set(branchRef, {
      branchName: name,
      baseBranchName: branch.baseBranchName ?? null,
      headCommitId,
      stateUpdate: encodeUpdateBase64(head),
      eventCount: countEvents(docToString(loadDoc(head))),
      deviceId,
      updatedAt: serverTimestamp(),
    });
    return { conflict: false };
  });
}
